import { capture } from '../posthog';

const MIGRATION_KEY = 'melodiza:migration-version';
const TARGET_VERSION = 2;
const LIKED_PLAYLISTS_KEY = 'melodiza:liked-playlists';
const ONBOARDED_KEY = 'melodiza:onboarded';

// Inline mapping — do NOT import from businessPlaylists.js to keep migration self-contained
const SEED_BY_BUSINESS = {
  cafe: ['morning-coffee', 'modern-days', 'zen-wellness'],
  restaurant: ['soir-elegance', 'morning-coffee', 'cocktail-time'],
  bar: ['cocktail-time', 'soir-elegance', 'movement'],
  salon: ['modern-days', 'morning-coffee', 'zen-wellness'],
  boutique: ['modern-days', 'morning-coffee', 'cocktail-time'],
  spa: ['zen-wellness', 'morning-coffee', 'soir-elegance'],
  gym: ['movement', 'cocktail-time', 'modern-days'],
  hotel: ['soir-elegance', 'morning-coffee', 'modern-days'],
  office: ['modern-days', 'morning-coffee', 'zen-wellness'],
};

/**
 * runMigrationV2 — idempotent one-shot migration for existing early-access users.
 *
 * If the user has already onboarded and has no liked playlists, seeds
 * 3 playlists matching their businessType into melodiza:liked-playlists.
 *
 * Returns a result object describing what happened:
 * - { applied: true, businessType, seededCount, requiresBanner: true }
 * - { skipped: true, reason, requiresBanner?: true }
 */
export function runMigrationV2(opts = {}) {
  const enabled = opts.enabled ?? (import.meta.env.VITE_MIGRATION_V2_ENABLED !== 'false');
  if (!enabled) return { skipped: true, reason: 'flag_disabled' };

  let currentVersion = 0;
  try {
    currentVersion = parseInt(localStorage.getItem(MIGRATION_KEY) || '0', 10);
  } catch {
    // ignore read errors
  }
  if (currentVersion >= TARGET_VERSION) return { skipped: true, reason: 'already_migrated' };

  const onboardedRaw = localStorage.getItem(ONBOARDED_KEY);
  if (!onboardedRaw) {
    // New user — no migration needed, just bump version
    try { localStorage.setItem(MIGRATION_KEY, String(TARGET_VERSION)); } catch { /* ignore */ }
    return { skipped: true, reason: 'new_user_no_onboarding' };
  }

  let businessType = null;
  try {
    const parsed = JSON.parse(onboardedRaw);
    businessType = typeof parsed === 'object' && parsed !== null ? parsed.businessType : null;
  } catch {
    try { localStorage.setItem(MIGRATION_KEY, String(TARGET_VERSION)); } catch { /* ignore */ }
    capture('migration_v2_skipped', { reason: 'corrupted_onboarded' });
    return { skipped: true, reason: 'corrupted_onboarded' };
  }

  if (!businessType || !SEED_BY_BUSINESS[businessType]) {
    try { localStorage.setItem(MIGRATION_KEY, String(TARGET_VERSION)); } catch { /* ignore */ }
    capture('migration_v2_skipped', { reason: 'no_business_type', businessType });
    return { skipped: true, reason: 'no_business_type', requiresBanner: true };
  }

  let existingLikes = [];
  try {
    existingLikes = JSON.parse(localStorage.getItem(LIKED_PLAYLISTS_KEY) || '[]');
  } catch {
    existingLikes = [];
  }
  if (existingLikes.length > 0) {
    try { localStorage.setItem(MIGRATION_KEY, String(TARGET_VERSION)); } catch { /* ignore */ }
    return { skipped: true, reason: 'already_has_likes' };
  }

  const seedIds = SEED_BY_BUSINESS[businessType];
  try {
    localStorage.setItem(LIKED_PLAYLISTS_KEY, JSON.stringify(seedIds));
    localStorage.setItem(MIGRATION_KEY, String(TARGET_VERSION));
  } catch {
    capture('migration_v2_skipped', { reason: 'storage_error' });
    return { skipped: true, reason: 'storage_error' };
  }
  capture('migration_v2_applied', { businessType, seededCount: seedIds.length });

  return { applied: true, businessType, seededCount: seedIds.length, requiresBanner: true };
}
